<template>
  <div id="app" class="container">

    <!-- Tester row -->
    <div>
      <h1 class="display-3 text-center title-custom">
        Eighty App Tester
        <img height="50px" src="./logo.png">
      </h1>
    </div>
    <div class="row spacing-custom">
      <div class="col">
        <div id="codeEditor"></div>
        <h5 style> Metadata </h5>
        <div class="input-group">
          <input type="text" class="form-control" style="margin-bottom: 5px;" placeholder="{}" v-model="metadata">
        </div> 
        <h5 style> Options </h5>
        <div class="input-group">
          <input type="text" class="form-control" style="margin-bottom: 5px;" placeholder="{}" v-model="options">
        </div> 
        <div class="input-group">
          <span class="input-group-btn">
            <button :class="buttonClass" :disabled="!url || crawling" @click="crawl">{{ buttonText }}</button>
          </span>
          <select v-model="requestType">
            <option disabled value="">Please select one</option>
            <option>default</option> 
            <option>headless</option>
            <option>proxy</option>
          </select>
          <input type="text" class="form-control" placeholder="http://www.google.com" v-model="url"  @keyup.enter="crawl">
          <button type="button" class="btn btn-danger" @click="reset">Reset</button>
        </div>
      </div>
    </div>

    <!-- Output row -->
    <h1 class="display-4 text-left title-custom">Output
      <img v-if="displayLoading" class="loading-custom" src="./loading.gif" />
      <h5 :class="displayOutputStatus.class">{{displayOutputStatus.text}}</h5>
    </h1>
    <div class="row spacing-custom">
      <div class="col results" id="outputEditor"></div>
    </div>

    <!-- Results row -->
    <h1 class="display-4 text-left title-custom">Results
      <img v-if="displayLoading" class="loading-custom" src="./loading.gif" />
      <h5 :class="displayResultStatus.class">{{displayResultStatus.text}}</h5>
    </h1>
    <ul class="nav nav-tabs">
      <li class="nav-item">
        <a href="#" :class="isActive('processDocument')" @click.prevent="selected = 'processDocument'">ProcessDocument</a>
      </li>
      <li class="nav-item">
        <a href="#" :class="isActive('parseLinks')" @click.prevent="selected = 'parseLinks'">ParseLinks</a>
      </li>
    </ul> 
    <div class="row spacing-custom">
      <div class="col" :hidden="selected !== 'processDocument'">
        <div class="results">
            <pre>{{ JSON.stringify(results.processDocument, null, 4) }}</pre>
        </div>
      </div>
      <div class="col" :hidden="selected !== 'parseLinks'">
        <div class="results">
          <span :hidden="results.parseLinks.length < 1">Number of ParsedLinks: {{ numParseLinks }}</span>
          <br :hidden="results.parseLinks.length < 1">
          [
          <div class="link" v-for="(link, i) in results.parseLinks" :key="i">  <a :href="link" @click.prevent="url = link">  {{ link }}</a>,</div>
          ]
        </div>
      </div>
    </div>

    <!-- HTML Row -->
    <h1 class="display-4 text-left title-custom">Content
      <img v-if="displayLoading" class="loading-custom" src="./loading.gif" />
      <h5 :class="displayContentStatus.class">{{displayContentStatus.text}}</h5>
    </h1>
    <div class="row spacing-custom">
      <div id="contentEditor" class="col html"></div>
    </div>
  </div>
</template>

<script>
// Ace imports
import ace from 'brace';
import 'brace/ext/searchbox';
import 'brace/mode/javascript';
import 'brace/mode/json';
import 'brace/mode/text';
import 'brace/mode/html';
import 'brace/theme/monokai';
import 'brace/theme/chrome';
import 'brace/theme/tomorrow_night';

import template from './app_template.js';
import axios from 'axios-proxy-fix';
import util from 'util';
import config from './config.js';

const APP_CONTENT_DATA = 'APP_CONTENT_DATA';

export default {
  name: 'app',
  
  // set up data for our vue instance
  data () {
    return {
      displayLoading: false,
      displayOutputStatus: {text: '', class: 'text-success'}, 
      displayResultStatus:  {text: '', class: 'text-success'},
      displayContentStatus:  {text: '', class: 'text-success'},
      outputEditor: null,
      codeEditor: null,
      contentEditor: null,
      requestType: 'default', 
      results: {
        processDocument: {},
        parseLinks: []
      },
      url: '',
      metadata: "{}",
      options: "{}",
      crawling: false,
      buttonClass: 'btn btn-primary',
      selected: 'processDocument'
    };
  },

  methods: {
    // changes the active tab for the editor
    // TODO: implement a way for users to change this
    changeTheme (themeName = 'chrome') {
      this.codeEditor.setTheme(`ace/theme/${themeName}`);
    },

    reset () {
      this.resetEditors();
      this.codeEditor.setValue(template);
      localStorage.removeItem(APP_CONTENT_DATA);
    },

    // sends the 80app and url to '/test' and crawls the page,
    // and then displays the results (or errors)
    crawl () {
      this.resetEditors();
      this.displayLoading = true;
      this.buttonClass = 'btn btn-primary';

      let { url, requestType, metadata, options } = this;

      url = url.trim();

      if (metadata === "") {
        metadata = {};
      } else {
        try {
          metadata = JSON.parse(metadata);
        } catch(error) {
          this.outputEditor.setValue('Invalid JSON for Metadata');
          this.clearSpinning();
          return;
        }
      }

      if (options === "") {
        options = {};
      } else {
        try {
          options = JSON.parse(options);
        } catch(error) {
          this.outputEditor.setValue('Invalid JSON for Options');
          this.clearSpinning();
          return;
        }
      }

      let url_obj;
      // Attempt to parse url as stringified url oject { url: 'url', options: {} }
      try {
        let tmp = JSON.parse(url);
        if (typeof tmp.url === 'string' && tmp.options) {
          url_obj = tmp;
        }
      } catch(e) {
        //
      }
      // Otherwise url is string, turn into url object 
      if (!url_obj) {
        if (!/^\{/.test(url)) {
          url_obj = { url: url, options: options };
        } else {
          this.outputEditor.setValue('Invalid url object.');
          this.clearSpinning();
          return;
        }
      }

      let app = this.codeEditor.getValue();
      let payload = { url_obj , app, requestType, metadata};

      //TODO: Learn how to fix this. 
      if(requestType === 'default') {
        payload.requestType = 'admin';
      }

      this.crawling = true;
      
      // Store the content of the app in local storage incase the user loses their tab
      localStorage.setItem(APP_CONTENT_DATA, app);

      axios.post(`${config.hostURL}/test`, payload, { timeout: 150000 })
        .then(({ data }) => {

          this.displayLoading = false;
          this.crawling = false;
          this.buttonClass = 'btn btn-success';

          let output = [];
          let { requestLatency, appLatency, processDocument, parseLinks, html, errors, numParseLinks } = data;

          if (data.output) {
            output = data.output;
          }

          if (errors.length > 0) {
            this.displayOutputStatus.text = 'Warning -- Errors';
            this.displayOutputStatus.class = 'text-warning';
            output.push('\nErrors that would show up in results file:');
            
            errors.forEach(error => output.push(`  ${util.inspect(error)}`));
          }

          if (requestLatency || appLatency) {
            output.push('\nBenchmarks:');

            if (requestLatency) {
              output.push(`  Request took ${requestLatency}ms`);
            }

            if (appLatency) {
              output.push(`  App took ${appLatency}ms`);

            }
          }

          output = output.join('\n');

          this.outputEditor.setValue(output);
          this.outputEditor.clearSelection();

          if (processDocument) {
            this.displayResultStatus.text = 'Success';
            this.displayResultStatus.class = 'text-success';
            this.results.processDocument = JSON.parse(processDocument);
          }

          if (parseLinks) {
            this.displayResultStatus.text = 'Success';
            this.displayResultStatus.class = 'text-success';
            this.results.parseLinks = parseLinks;
            this.numParseLinks = numParseLinks;
          }

          if (html) {
            this.displayContentStatus.text += 'Success';
            this.displayContentStatus.class = 'text-success';
            
            if (typeof html === 'object') {
              this.contentEditor.getSession().setMode('ace/mode/json');
              this.contentEditor.setValue(JSON.stringify(html, null, 4));
            } else {
              this.contentEditor.getSession().setMode('ace/mode/html');
              this.contentEditor.setValue(html);
            }

            this.contentEditor.clearSelection();
          } else {
            this.displayContentStatus.text += 'Failure';
            this.displayContentStatus.class = 'text-danger';
          }
        })
        .catch(err => {
          this.displaySuccess = false;
          // this handler will only be called if the 80app tester is at fault
          this.crawling = false;
          this.buttonClass = 'btn btn-danger';

          let errors = err;

          // if the err object has a `response` property then that means a response was returned from the backend
          // Otherwise the error occured on the frontend.
          if (err.response) {
            errors = err.response.data.errors;
            errors.unshift('The following error is an 80app tester backend bug. Notify the infrastructure channel.');
            
            errors = errors.join('\n');
          } else {

            errors = 'The following error is an 80app tester frontend bug. Notify the infrastructure channel.\n';
            let errorMsg = `Error: ${err.message}`;
            if (err.stack) {
              errorMsg = err.stack;
            }

            errors += errorMsg;
          }
        
          this.outputEditor.setValue(errors);
          this.outputEditor.clearSelection();
        });
    },

    // resets the state of our editors to empty
    resetEditors() {
      this.clearSpinning();
      this.results.processDocument = {};
      this.results.parseLinks = [];
      this.outputEditor.setValue('');
      this.contentEditor.setValue('');
    },

    clearSpinning() {
      this.displayLoading = false;

      this.displayOutputStatus.text = '';
      this.displayOutputStatus.class = 'text-success';

      this.displayResultStatus.text = ''; 
      this.displayResultStatus.class = 'text-success';

      this.displayContentStatus.text = '';
      this.displayContentStatus.class = 'text-success';
    },

    // used to give our tabs the proper class
    isActive (tab) {
      if (this.selected === tab) {
        return 'nav-link active';
      } else {
        return 'nav-link';
      }
    }
  },

  computed: {
    // used to display different values for our 'Crawl'
    // button depending on the current state of our app
    buttonText () {
      if (this.crawling) {
        return 'Crawling...';
      } else {
        return 'Crawl';
      }
    }
  },

  // set up all of our editors on mount
  mounted () {
    this.codeEditor = ace.edit('codeEditor');
    this.codeEditor.getSession().setMode('ace/mode/javascript');
    this.codeEditor.setTheme('ace/theme/monokai');
    this.codeEditor.setShowPrintMargin(false);

    this.outputEditor = ace.edit('outputEditor');
    this.outputEditor.getSession().setMode('ace/mode/text');
    this.outputEditor.setTheme('ace/theme/tomorrow_night');
    this.outputEditor.setFontSize('16px');
    this.outputEditor.setReadOnly(true);
    this.outputEditor.setShowPrintMargin(false);

    this.contentEditor = ace.edit('contentEditor');
    this.contentEditor.setTheme('ace/theme/chrome');
    this.contentEditor.setReadOnly(true);
    this.contentEditor.setShowPrintMargin(false);

    // Check to see if we have any 80app content stored in local storage
    let appContent = localStorage.getItem(APP_CONTENT_DATA);

    if (appContent) {
      this.codeEditor.setValue(appContent);
    } else {
      this.codeEditor.setValue(template);
    }

    this.codeEditor.clearSelection();
    this.codeEditor.moveCursorToPosition({row: 0, col: 0});
  }
};
</script>

<style>
#codeEditor {
  height: 700px;
  box-shadow: 5px 5px 10px #888888;
  margin-bottom: 15px;
  max-height: 1400px;
}

h1 {
  padding-bottom: 10px;
}

.results {
  height: 400px;
  box-shadow: 0px 0px 20px #888;
  overflow: scroll;
}

.html {
  height: 600px;
  box-shadow: 0px 0px 20px #888;
}

.row {
  margin-bottom: 100px;
}

#outputEditor {
  overflow: hidden;
}

.link {
  padding-left: 20px;
}

.reset-button {
  margin-top: 10px;
}

.title-custom {
  font-family: 'Nova Flat', cursive;
  color: #251E60;
}

.spacing-custom {
  margin-bottom: 1%;
}

.loading-custom {
  height: 40px;
}

</style>



// WEBPACK FOOTER //
// App.vue?5041980d